import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { getDefaultAuthPath, storePendingSignup } from '../services/authBootstrapService';
import { validateReferralCode } from '../services/referralService';
import { activateOnEnterOrSpace } from '../utils/a11y';
import Footer from '../components/Footer';

const ROLE_THEMES = {
  admin: { bg: '#F0F4F8', card: '#fff', accent: '#3B82F6', text: '#1a1a2e', muted: '#7B8CA3', border: 'rgba(59,130,246,0.12)', inputBg: '#F7F9FC' },
  spl:   { bg: '#F0F4F8', card: '#fff', accent: '#3B82F6', text: '#1a1a2e', muted: '#7B8CA3', border: 'rgba(59,130,246,0.12)', inputBg: '#F7F9FC' },
  annotator: { bg: '#FDFBF7', card: '#fff', accent: '#5DCAA5', text: '#1a1a1a', muted: '#999', border: 'rgba(0,0,0,0.08)', inputBg: '#FDFBF7' },
};

// Roles that can self-serve signup via the public /signup form.
// Admin + SPL are invite-only — contact founders@juuniai.com.
const PUBLIC_SIGNUP_ROLES = new Set(['annotator']);

export default function Signup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('annotator');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralValid, setReferralValid] = useState(null); // null=unchecked, { valid, referrerName } or { valid: false, error }
  const [referralChecking, setReferralChecking] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signInWithGoogle } = useAuth();

  // Read ?ref= from URL on mount
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      checkReferralCode(refCode);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkReferralCode(code) {
    if (!code || code.trim().length < 4) {
      setReferralValid(null);
      return;
    }
    setReferralChecking(true);
    const result = await validateReferralCode(code);
    setReferralValid(result);
    setReferralChecking(false);
  }

  const t = ROLE_THEMES[role] || ROLE_THEMES.annotator;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    // Defense in depth: block non-public-signup roles server-side even if UI is
    // bypassed. Admin + SPL signup is invite-only; contact founders@juuniai.com.
    if (!PUBLIC_SIGNUP_ROLES.has(role)) {
      // Track D dive #38: error path matches the deflection — point at
      // /request-access so the prospect lands on a structured form, not
      // a mailto. Defense-in-depth: the role tile click already routes
      // there; this catches a tampered-button bypass attempt.
      setError('Admin and Project Lead accounts are invite-only. Use the "Request access" link below — captures your details for our pipeline.');
      return;
    }
    setLoading(true);
    try {
      const redirectPath = getDefaultAuthPath(role);
      const emailRedirectTo = window.location.origin + redirectPath;
      storePendingSignup({ name, company, role, email, referrerId: referralValid?.referrerId || null });
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email, password,
        options: { emailRedirectTo, data: { name, company, role } },
      });
      if (authError) throw authError;
      if (authData.session?.user) {
        navigate(redirectPath);
      } else {
        setNotice('Check your email to verify your account. After confirmation, sign in to continue setup.');
      }
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    try {
      setError('');
      setNotice('');
      await signInWithGoogle({
        redirectPath: getDefaultAuthPath(role),
        pendingSignup: { name, company, role, email, referrerId: referralValid?.referrerId || null },
      });
    } catch (err) {
      setError(err.message || 'Google signup failed');
    }
  };

  const inp = {
    width: '100%', padding: '12px 14px', border: `1px solid ${t.border}`,
    borderRadius: 8, fontSize: 14, fontFamily: "'DM Sans', sans-serif",
    outline: 'none', background: t.inputBg, color: t.text, boxSizing: 'border-box',
    transition: 'border-color 0.3s',
  };
  const lbl = {
    display: 'block', fontSize: 12, fontFamily: "'DM Mono', monospace",
    color: t.muted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6,
    transition: 'color 0.3s',
  };

  return (
    <div style={{
      position: 'relative',  // Phase 19.4 audit-fix: enable absolute-positioned Footer
      minHeight: '100dvh', background: t.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif",
      transition: 'background 0.4s ease',
      // Phase 14.4 — guarantee horizontal padding even on narrow viewports
      // so the card doesn't touch the edges on phones.
      padding: '20px 16px',
      boxSizing: 'border-box',
    }}>
      <div style={{
        // Phase 14.4 — was `width: 440` which overflowed at <440px
        // viewports; now responsive: card grows to 100% but capped at 440.
        // boxSizing:border-box keeps padding inside the cap.
        width: '100%', maxWidth: 440, padding: 48, background: t.card, borderRadius: 16,
        border: `1px solid ${t.border}`, boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
        transition: 'border-color 0.4s ease',
        boxSizing: 'border-box',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {/* Phase 14.7 — semantic h1 (was a styled div). Visual styling
              preserved on the h1 element so the brand mark renders the
              same; SR users now hear it as the page heading. */}
          <h1 style={{
            fontSize: 32, fontWeight: 700, color: t.accent,
            letterSpacing: '0.15em', fontFamily: "'DM Mono', monospace",
            marginBottom: 6, marginTop: 0, transition: 'color 0.4s ease',
          }}>JUUNI AI</h1>
          <div style={{
            fontSize: 10, color: t.muted, fontFamily: "'DM Mono', monospace",
            letterSpacing: '0.08em', textTransform: 'uppercase',
            transition: 'color 0.4s ease',
          }}>Create Your Account</div>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--bg-error)', border: '1px solid var(--border-error)', borderRadius: 8, color: 'var(--text-error)', fontSize: 13, marginBottom: 20 }}>{error}</div>
        )}
        {notice && (
          <div style={{ padding: '10px 14px', background: 'var(--bg-success)', border: '1px solid var(--border-success)', borderRadius: 8, color: 'var(--text-success)', fontSize: 13, marginBottom: 20 }}>{notice}</div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={lbl} htmlFor="signup-name">Full Name</label>
            <input id="signup-name" style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Don Lim" required autoComplete="name" />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={lbl} htmlFor="signup-company">Company</label>
            <input id="signup-company" style={inp} value={company} onChange={e => setCompany(e.target.value)} placeholder="e.g. Acme Annotations" required autoComplete="organization" />
          </div>

          {/* Referral code */}
          <div style={{ marginBottom: 16 }}>
            <label style={lbl} htmlFor="signup-referral">Referral Code (optional)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input id="signup-referral" style={{ ...inp, flex: 1 }} value={referralCode} onChange={e => { setReferralCode(e.target.value); setReferralValid(null); }} placeholder="e.g. MAYA-FOLEY-2026" />
              {referralCode.length >= 4 && !referralValid && !referralChecking && (
                <button type="button" onClick={() => checkReferralCode(referralCode)} style={{ padding: '8px 14px', fontSize: 12, fontFamily: "'DM Mono', monospace", background: t.accent, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' }}>Check</button>
              )}
            </div>
            {referralChecking && <div style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: t.muted, marginTop: 4 }}>Checking...</div>}
            {referralValid?.valid && (
              <div style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: t.accent, marginTop: 4 }}>
                Referred by {referralValid.referrerName} ({referralValid.referrerDomain})
              </div>
            )}
            {referralValid && !referralValid.valid && (
              <div style={{ fontSize: 10, fontFamily: "'DM Mono', monospace", color: 'var(--text-error)', marginTop: 4 }}>
                {referralValid.error || 'Invalid referral code'}
              </div>
            )}
          </div>

          {/* Role selector — Annotator self-serve; Admin + SPL are invite-only */}
          <div style={{ marginBottom: 16 }}>
            <div style={lbl} role="group" aria-label="I am a...">I am a...</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                // Track D dive #49 (CRITIQUE-driven, 2026-05-04): replaced
                // ambiguous "Invite only" desc with explicit "Request access →"
                // for invite-only tiles. Pre-fix the tile LOOKED dimmed
                // (opacity 0.55) but cursor was pointer — split signal.
                // Now the desc IS the click affordance, removing the
                // visual contradiction.
                { value: 'admin', label: 'Admin / CEO', desc: 'Request access →', icon: '⚡', inviteOnly: true },
                { value: 'spl', label: 'Project Lead', desc: 'Request access →', icon: '⚡', inviteOnly: true },
                { value: 'annotator', label: 'Annotator', desc: 'Find & annotate', icon: '🎯', inviteOnly: false },
              ].map(r => {
                const disabled = r.inviteOnly;
                return (
                  <button
                    key={r.value}
                    type="button"
                    aria-disabled={disabled}
                    title={disabled ? 'SPL/Admin signup is invite-only. Click to request access — captures your details for our pipeline.' : undefined}
                    // Track D dive #38 (FIND-003 fix, 2026-05-04): when an
                    // invite-only role is clicked, route the prospect to
                    // /request-access where their info is captured into the
                    // request_access_submission table (lead pipeline) instead
                    // of opening a mailto: that loses structured data and has
                    // higher bounce rate. The query param tags origin so the
                    // form can pre-select the team-size / role context.
                    onClick={() => {
                      if (disabled) {
                        navigate(`/request-access?from=signup&intended_role=${r.value}`);
                        return;
                      }
                      setRole(r.value);
                    }}
                    style={{
                      flex: 1, padding: '10px 8px', borderRadius: 10,
                      cursor: 'pointer',
                      opacity: disabled ? 0.55 : 1,
                      border: `1.5px solid ${role === r.value ? (ROLE_THEMES[r.value]?.accent || t.accent) : t.border}`,
                      background: role === r.value ? `${ROLE_THEMES[r.value]?.accent || t.accent}10` : 'transparent',
                      textAlign: 'center', transition: 'all 0.3s',
                    }}>
                    <div style={{ fontSize: 16, marginBottom: 2 }}>{r.icon}</div>
                    <div style={{ fontSize: 12, fontWeight: role === r.value ? 600 : 400, color: role === r.value ? (ROLE_THEMES[r.value]?.accent || t.accent) : t.text, fontFamily: "'DM Mono', monospace", transition: 'color 0.3s' }}>{r.label}</div>
                    <div style={{ fontSize: 10, color: t.muted, fontFamily: "'DM Mono', monospace", marginTop: 2 }}>{r.desc}</div>
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 10, color: t.muted, fontFamily: "'DM Mono', monospace", marginTop: 8, textAlign: 'center' }}>
              {/* Track D dive #38: deflection now points at /request-access
                  (structured lead form) instead of mailto: — captures team
                  size + pain context so Don can prioritize follow-up by
                  fit, not by inbox-search luck. */}
              Admin / Project Lead access?{' '}
              <a
                href="/request-access?from=signup"
                onClick={(e) => { e.preventDefault(); navigate('/request-access?from=signup'); }}
                style={{ color: t.accent, textDecoration: 'underline' }}
              >
                Request access →
              </a>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={lbl} htmlFor="signup-email">Email</label>
            <input id="signup-email" style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required autoComplete="email" maxLength={254} />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={lbl} htmlFor="signup-password">Password</label>
            <input id="signup-password" style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Minimum 6 characters" required minLength={6} maxLength={72} autoComplete="new-password" />
          </div>

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '12px 0', background: t.accent, color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
            fontFamily: "'DM Sans', sans-serif", transition: 'background 0.4s ease, opacity 0.15s',
          }}>{loading ? 'Creating account...' : 'Create Account'}</button>
        </form>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '24px 0' }}>
          <div style={{ flex: 1, height: 1, background: t.border, transition: 'background 0.4s' }} />
          <span style={{ fontSize: 12, color: t.muted, fontFamily: "'DM Mono', monospace" }}>OR</span>
          <div style={{ flex: 1, height: 1, background: t.border, transition: 'background 0.4s' }} />
        </div>

        <button onClick={handleGoogleSignup} style={{
          width: '100%', padding: '12px 0', background: '#fff', color: t.text,
          border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 14,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          fontFamily: "'DM Sans', sans-serif", transition: 'border-color 0.4s',
        }}>
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          Continue with Google
        </button>

        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: t.muted }}>
          Already have an account?{' '}
          <span
            role="button"
            tabIndex={0}
            onClick={() => navigate('/login')}
            onKeyDown={activateOnEnterOrSpace(() => navigate('/login'))}
            style={{ color: t.accent, cursor: 'pointer', fontWeight: 500, transition: 'color 0.4s' }}>Sign in</span>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 10, color: t.muted, fontFamily: "'DM Mono', monospace", letterSpacing: '0.05em' }}>
          juuniai.com
        </div>
      </div>
      {/* Phase 19.4 audit-fix (2026-04-29): legal/security links footer per scope §6 + GDPR Article 13.
          Absolute-positioned at page bottom so it doesn't disrupt the centered card. */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <Footer variant="auth" />
      </div>
    </div>
  );
}
